import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import React, { Component } from 'react';
import { Alert, Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import createModalToggle from '../../generic/create-modal-toggle';
import Loading from '../../generic/Loading';
import Tabs from '../../generic/Tabs';
import { campaignProductMetaShape, creativeShape } from '../../prop-types';
import AddCreativesButton from '../AddCreativesButton';
import { creativesTypes } from '../constants';
import ContentfulCreativeTab from '../ContentfulCreatives/ContentfulCreativesTab';
import ListCreatives from '../ListCreatives';

export const CONFIRMATION_MESSAGE =
  'All unsaved changes will be discarded. Do you really wish to exit without saving?';

class CreativesModalContent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: creativesTypes.SC_CREATIVES,
    };
    this.handleSaveAll = this.handleSaveAll.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleSaveContentfulCreatives =
      this.handleSaveContentfulCreatives.bind(this);
  }

  componentDidMount() {
    this.props.initialize();
    this.setState({ activeTab: creativesTypes.SC_CREATIVES });
  }

  // This is added to avoid unnecessary re renders of this and corresponding child components
  // due to change in flashMessage in redux store
  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.creatives, nextProps.creatives) ||
      !isEqual(this.props.contentfulCreatives, nextProps.contentfulCreatives) ||
      !isEqual(this.state.activeTab, nextState.activeTab)
    );
  }

  componentWillUnmount() {
    this.props.onExit();
  }

  handleTabChange(activeTab) {
    this.setState({ activeTab });
  }

  handleSaveAll() {
    const { onSaveAll, toggle, setConfirmationMessage, onClose } = this.props;

    onSaveAll().then(() => {
      setConfirmationMessage(false);
      toggle(false);
      onClose();
    });
  }

  handleSaveContentfulCreatives = async event => {
    const { campaignSave, toggle, onSaveAllContentfulCreatives } = this.props;

    try {
      await onSaveAllContentfulCreatives();
      await campaignSave(event);
      toggle(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving contentful creatives:', error);
    }
  };

  handleUpdate(...args) {
    this.props.setConfirmationMessage(CONFIRMATION_MESSAGE);
    this.props.onUpdate(...args);
  }

  renderContent() {
    const {
      initialized,
      onDelete,
      resizeCreativeImage,
      creatives,
      campaignRelatedProducts,
      opportunityName,
      campaignId,
      onAddMultiple,
      campaignProductTags,
    } = this.props;

    if (!initialized) {
      return <Loading />;
    }

    const noCreatives = isEmpty(creatives);

    if (noCreatives) {
      return (
        <Alert color="warning" className="my-3">
          Please upload some creatives for the campaign.
        </Alert>
      );
    }
    return (
      <ListCreatives
        onDelete={onDelete}
        resizeCreativeImage={resizeCreativeImage}
        onUpdate={this.handleUpdate}
        creatives={creatives}
        opportunityName={opportunityName}
        campaignId={campaignId}
        campaignRelatedProducts={campaignRelatedProducts}
        onSuccess={onAddMultiple}
        campaignProductTags={campaignProductTags}
      />
    );
  }

  render() {
    const {
      creatives,
      onAddMultiple,
      opportunityId,
      toggle,
      campaignId,
      contentfulCreatives,
      campaignLineProducts,
      campaignProductTags,
      onAddContentfulCreatives,
      onUpdateContentfulCreatives,
      onChangeCampaign,
      campaignProductMetas,
      onDeleteContentfulCreatives,
      campaignSave,
    } = this.props;
    const { activeTab } = this.state;

    const hasCreatives = !isEmpty(creatives);
    const hasContentfulCreatives = !isEmpty(contentfulCreatives);
    const tabs = [
      {
        label: 'Sales Cockpit Creatives',
        value: creativesTypes.SC_CREATIVES,
        content: this.renderContent(),
      },
      {
        label: 'Contentful Creatives',
        value: creativesTypes.CONTENTFUL_CREATIVES,
        content: (
          <ContentfulCreativeTab
            onChangeCampaign={onChangeCampaign}
            campaignLineProducts={campaignLineProducts}
            campaignProductTags={campaignProductTags}
            contentfulCreatives={contentfulCreatives}
            onUpdateContentfulCreatives={onUpdateContentfulCreatives}
            campaignProductMetas={campaignProductMetas}
            onSuccess={onAddContentfulCreatives}
            onDelete={onDeleteContentfulCreatives}
            onSave={campaignSave}
            campaignId={campaignId}
          />
        ),
        disabled: !campaignId,
      },
    ];
    return (
      <>
        <ModalHeader toggle={toggle} className="w-100 border-bottom-0">
          Creatives
        </ModalHeader>
        <ModalBody>
          <Tabs
            tabs={tabs}
            id="creativeModal"
            onTabChange={this.handleTabChange}
            defaultActive={creativesTypes.SC_CREATIVES}
            enableRouteBasedState={false}
          />
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div>
            {activeTab === creativesTypes.SC_CREATIVES && (
              <AddCreativesButton
                opportunityId={opportunityId}
                onSuccess={onAddMultiple}
              />
            )}
          </div>
          {hasCreatives && activeTab === creativesTypes.SC_CREATIVES && (
            <Button color="primary" onClick={this.handleSaveAll}>
              Save
            </Button>
          )}
          {hasContentfulCreatives &&
            activeTab === creativesTypes.CONTENTFUL_CREATIVES && (
              <div className="ms-auto">
                <Button
                  color="primary"
                  type="submit"
                  onClick={this.handleSaveContentfulCreatives}
                >
                  Save
                </Button>
              </div>
            )}
        </ModalFooter>
      </>
    );
  }
}

CreativesModalContent.propTypes = {
  creatives: arrayOf(creativeShape.isRequired).isRequired,
  initialized: bool.isRequired,
  onAddMultiple: func.isRequired,
  onAddContentfulCreatives: func.isRequired,
  onUpdateContentfulCreatives: func.isRequired,
  onSaveAllContentfulCreatives: func.isRequired,
  onDelete: func.isRequired,
  resizeCreativeImage: func.isRequired,
  initialize: func.isRequired,
  onExit: func.isRequired,
  onUpdate: func.isRequired,
  opportunityId: number.isRequired,
  onClose: func,
  toggle: func.isRequired,
  setConfirmationMessage: func.isRequired,
  onSaveAll: func.isRequired,
  campaignRelatedProducts: bool,
  opportunityName: string.isRequired,
  campaignId: number,
  onChangeCampaign: func.isRequired,
  campaignProductTags: arrayOf(string),
  campaignLineProducts: arrayOf(string),
  campaignProductMetas: arrayOf(campaignProductMetaShape.isRequired),
  contentfulCreatives: arrayOf(object), // eslint-disable-line react/forbid-prop-types
  campaignSave: func.isRequired,
  onDeleteContentfulCreatives: func.isRequired,
};

CreativesModalContent.defaultProps = {
  onClose: () => {},
  campaignRelatedProducts: false,
  campaignProductTags: [],
  campaignLineProducts: [],
  campaignProductMetas: [],
  campaignId: null,
  contentfulCreatives: undefined,
};

const CreativesModal = createModalToggle(
  'Creatives',
  CreativesModalContent,
  { color: 'secondary' },
  { size: 'xl', className: 'modal-dialog-extra-wide modal-dialog-scrollable' },
);

export default CreativesModal;
export { CreativesModalContent }; // for unit test
